import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type CSSProperties,
} from 'react';
import { ORIGINAL_FLARE, type FlareStyle } from './effects/flare/options';
import type { Renderer } from './effects/adaptive/renderer';
import type {
  QualityPreference,
  QualityState,
} from './effects/adaptive/quality';
import { deferEffectUntilPageSettles } from './performance';

export function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const query = matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);
  return reduced;
}

export function Background({
  enabled,
  paused,
  quality,
  onStatus,
}: {
  enabled: boolean;
  paused: boolean;
  quality: QualityPreference;
  onStatus: (status: string) => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const renderer = useRef<Renderer | null>(null);
  const preference = useRef(quality);
  const pause = useRef(paused);
  preference.current = quality;
  pause.current = paused;
  useEffect(() => {
    if (!enabled || !canvas.current) {
      onStatus('Still background');
      return;
    }
    if (!navigator.gpu) {
      onStatus('Static fallback · WebGPU unavailable');
      return;
    }
    let cancelled = false;
    let cleanup = () => {};
    const start = () =>
      import('./effects/adaptive/renderer')
        .then(({ createRenderer }) => {
          if (cancelled || !canvas.current) return;
          const instance = createRenderer({
            canvas: canvas.current,
            initialPreference: preference.current,
            onError: () => onStatus('Quality signal unavailable'),
          });
          renderer.current = instance;
          const report = (state: QualityState) =>
            onStatus(
              `WebGPU · ${state.effective === 'high' ? 'High' : 'Low'}${state.preference === 'auto' ? ' / auto' : ''}`,
            );
          const unsubscribe = instance.subscribe(report);
          instance.setPaused(pause.current);
          cleanup = () => {
            unsubscribe();
            instance.dispose();
            if (renderer.current === instance) renderer.current = null;
          };
          return instance.ready.then(() => {
            if (!cancelled) report(instance.getState());
          });
        })
        .catch(() => {
          if (!cancelled) onStatus('Static fallback · WebGPU unavailable');
        });
    onStatus('Preparing WebGPU');
    const cancelStart = deferEffectUntilPageSettles(start, 1400, 2500);
    return () => {
      cancelled = true;
      cancelStart();
      cleanup();
    };
  }, [enabled, onStatus]);
  useEffect(() => {
    void renderer.current?.setPreference(quality);
  }, [quality]);
  useEffect(() => {
    renderer.current?.setPaused(paused);
  }, [paused]);
  return (
    <div className="ambient" aria-hidden="true">
      <canvas ref={canvas} />
      <div className="ambient-shade" />
    </div>
  );
}

export function FlareButton({
  children = 'Get Started',
  enabled,
  paused,
  onClick,
  compact = false,
  style = ORIGINAL_FLARE,
  ...buttonAttributes
}: {
  children?: ReactNode;
  enabled: boolean;
  paused: boolean;
  onClick: () => void;
  compact?: boolean;
  style?: FlareStyle;
  'data-test'?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const renderer = useRef<{
    dispose(): void;
    setPaused(value: boolean): void;
  } | null>(null);
  const pause = useRef(paused);
  const [ready, setReady] = useState(false);
  pause.current = paused;
  useEffect(() => {
    setReady(false);
    if (!enabled || !navigator.gpu || !canvas.current) return;
    let cancelled = false;
    let cleanup = () => {};
    const start = () =>
      import('./effects/flare/renderer')
        .then(({ createRenderer }) => {
          if (cancelled || !canvas.current) return;
          const instance = createRenderer({
            canvas: canvas.current,
            pointerTarget: window,
            style,
          });
          renderer.current = instance;
          instance.setPaused(pause.current);
          cleanup = () => {
            instance.dispose();
            if (renderer.current === instance) renderer.current = null;
          };
          return instance.ready.then(() => {
            if (!cancelled) setReady(true);
          });
        })
        .catch(() => {
          if (!cancelled) setReady(false);
        });
    const cancelStart = deferEffectUntilPageSettles(start, 2200, 3200);
    return () => {
      cancelled = true;
      cancelStart();
      cleanup();
    };
  }, [enabled, style]);
  useEffect(() => {
    renderer.current?.setPaused(paused);
  }, [paused]);
  return (
    <button
      {...buttonAttributes}
      className={`flare-button ${compact ? 'compact' : ''}`}
      onClick={onClick}
      style={{ '--flare-halo': `${style.halo}px` } as CSSProperties}
    >
      <canvas
        ref={canvas}
        aria-hidden="true"
        style={{ opacity: ready ? 1 : 0 }}
      />
      <span>{children}</span>
    </button>
  );
}
